import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useLayoutEffect, useMemo, } from "react";
import { FiChevronLeft } from "@react-icons/all-files/fi/FiChevronLeft";
import { FiChevronRight } from "@react-icons/all-files/fi/FiChevronRight";
import useSwipe from "hooks/useSwipe";
import "./_style.scss";
function ImageCarousel({ update }) {
    const { assets } = update;
    const [idx, setIdx] = useState(0);
    const containerRef = useRef(null);
    const trackRef = useRef(null);
    const validAssets = useMemo(() => assets.filter((asset) => asset.publicUrl), [assets]);
    const hasMultiple = validAssets.length > 1;
    const isFirst = idx === 0;
    const isLast = idx === validAssets.length - 1;
    const prev = () => setIdx((i) => Math.max(0, i - 1));
    const next = () => setIdx((i) => Math.min(validAssets.length - 1, i + 1));
    // Sync the track position whenever idx changes (enables the snap animation).
    // useLayoutEffect fires before paint so there is no visible jump.
    useLayoutEffect(() => {
        if (trackRef.current) {
            trackRef.current.style.transform = `translateX(-${idx * 100}%)`;
        }
    }, [idx]);
    const swipeHandlers = useSwipe({
        onSwipeLeft: next,
        onSwipeRight: prev,
        onDragMove: (offsetPx) => {
            if (!trackRef.current || !containerRef.current) {
                return;
            }
            // Disable CSS transition so the track follows the pointer in real time.
            containerRef.current.classList.add("swipeable-image-carousel--dragging");
            // Clamp at the boundaries so the first/last slide can't be pulled away from the edge.
            let clampedOffset = offsetPx;
            if (isFirst) {
                clampedOffset = Math.min(0, offsetPx);
            }
            else if (isLast) {
                clampedOffset = Math.max(0, offsetPx);
            }
            // translateX(calc(-n% + offsetPx)) — calc() must be INSIDE translateX().
            trackRef.current.style.transform = `translateX(calc(-${idx * 100}% + ${clampedOffset}px))`;
        },
        onDragEnd: (didSwipe) => {
            if (!containerRef.current) {
                return;
            }
            // Re-enable transition before the final snap.
            containerRef.current.classList.remove("swipeable-image-carousel--dragging");
            if (!didSwipe && trackRef.current) {
                // Threshold not met — animate back to the current slide.
                trackRef.current.style.transform = `translateX(-${idx * 100}%)`;
            }
            // If didSwipe, setIdx fires next which triggers useLayoutEffect to
            // animate forward from wherever the pointer was released.
        },
    });
    // All hooks must be called before any conditional return.
    if (validAssets.length === 0) {
        return null;
    }
    // Only allow dragging/swiping when there's more than one image.
    const dragHandlers = hasMultiple ? swipeHandlers : {};
    const containerClassName = `swipeable-image-carousel${hasMultiple ? " swipeable-image-carousel--draggable" : ""}`;
    return (_jsxs("div", { className: containerClassName, ref: containerRef, ...dragHandlers, "data-testid": "swipeable-image-carousel", children: [_jsx("img", { src: validAssets[0].publicUrl, alt: "", "aria-hidden": true, draggable: false, className: "swipeable-image-carousel__sizer" }), _jsx("div", { className: "swipeable-image-carousel__track", ref: trackRef, "data-testid": "swipeable-image-carousel-track", children: validAssets.map((asset, i) => {
                    var _a, _b;
                    return (_jsxs("div", { className: "swipeable-image-carousel__slide", children: [_jsx("img", { src: asset.publicUrl, alt: "", "aria-hidden": true, draggable: false, className: "swipeable-image-carousel__slide-bg" }), _jsx("img", { src: asset.publicUrl, alt: (_a = asset.filename) !== null && _a !== void 0 ? _a : `Photo ${i + 1}`, draggable: false, className: "swipeable-image-carousel__slide-img", "data-testid": `swipeable-image-carousel-slide-${i}` })] }, `update-image-${(_b = asset.id) !== null && _b !== void 0 ? _b : asset.filename}`));
                }) }), hasMultiple && (_jsxs(_Fragment, { children: [!isFirst && (_jsx("button", { type: "button", className: "swipeable-image-carousel__btn swipeable-image-carousel__btn--prev", onClick: prev, "aria-label": "Previous image", "data-testid": "swipeable-image-carousel-prev", children: _jsx(FiChevronLeft, {}) })), !isLast && (_jsx("button", { type: "button", className: "swipeable-image-carousel__btn swipeable-image-carousel__btn--next", onClick: next, "aria-label": "Next image", "data-testid": "swipeable-image-carousel-next", children: _jsx(FiChevronRight, {}) })), _jsx("div", { className: "swipeable-image-carousel__dots", "data-testid": "swipeable-image-carousel-dots", children: validAssets.map((asset, i) => {
                            var _a;
                            return (_jsx("button", { type: "button", className: `swipeable-image-carousel__dot${i === idx ? " swipeable-image-carousel__dot--active" : ""}`, onClick: () => setIdx(i), "aria-label": `Go to image ${i + 1}`, "data-testid": `swipeable-image-carousel-dot-${i}` }, `update-image-asset-button-${(_a = asset.id) !== null && _a !== void 0 ? _a : asset.filename}`));
                        }) }), _jsx("div", { className: "swipeable-image-carousel__counter", "data-testid": "swipeable-image-carousel-counter", children: `${idx + 1} / ${validAssets.length}` })] }))] }));
}
ImageCarousel.displayName = "ImageCarousel";
export default ImageCarousel;
